import { useEffect, useState } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from './IconButton';
import { IconDownload } from './icons';
import { useConfig } from '../context/ConfigContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

interface Transaction {
  id: number;
  contribution_date: string;
  employer_amount: number;
  employee_amount: number;
  interest_amount: number;
  reference: string | null;
  notes: string | null;
  running_balance: number;
}

interface MiniData {
  as_of: string;
  current_balance: number;
  retirement_balance: number;
  investment_balance: number;
  medisave_balance: number;
  transactions: Transaction[];
}

interface RangeData {
  opening_balance: number;
  transactions: Transaction[];
  closing_balance: number;
  retirement_balance: number;
  investment_balance: number;
  medisave_balance: number;
  totals: {
    employer_amount: number;
    employee_amount: number;
    interest_amount: number;
    retirement_amount: number;
    investment_amount: number;
    medisave_amount: number;
  };
}

// Shown alongside the combined balance on every tab — see Member::
// balanceAsOf()'s own comment on why these three never quite sum back up
// to the combined balance for a member with a pre-split opening_balance.
function AccountSplit({
  retirement,
  investment,
  medisave,
  currency,
}: {
  retirement: number;
  investment: number;
  medisave: number;
  currency: string;
}) {
  return (
    <div className="detail-grid">
      <div className="detail-item">
        <span className="detail-item-label">Retirement Account</span>
        <span className="detail-item-value">{formatMoney(retirement, currency)}</span>
      </div>
      <div className="detail-item">
        <span className="detail-item-label">Investment Account</span>
        <span className="detail-item-value">{formatMoney(investment, currency)}</span>
      </div>
      <div className="detail-item">
        <span className="detail-item-label">Medi-Save Account</span>
        <span className="detail-item-value">{formatMoney(medisave, currency)}</span>
      </div>
    </div>
  );
}

type Tab = 'mini' | 'detail' | 'annual';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

function TransactionTable({ transactions, currency }: { transactions: Transaction[]; currency: string }) {
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Employer</th>
            <th>Employee</th>
            <th>Interest</th>
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {transactions.length > 0 ? (
            transactions.map((tx) => (
              <tr key={tx.id}>
                <td>{tx.contribution_date}</td>
                <td>{formatMoney(tx.employer_amount, currency)}</td>
                <td>{formatMoney(tx.employee_amount, currency)}</td>
                <td>{formatMoney(tx.interest_amount, currency)}</td>
                <td>
                  <strong>{formatMoney(tx.running_balance, currency)}</strong>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="muted center">
                No transactions.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

interface StatementsPanelProps {
  memberId: number;
  memberName: string;
}

// The Mini/Detail/Annual tab UI over one member's contribution ledger —
// shared by the per-member "Statements" button on MemberDetail.tsx and the
// menu-level Statements page (which picks a member first, or resolves to
// the caller's own record for a self-service Member).
export function StatementsPanel({ memberId, memberName }: StatementsPanelProps) {
  const { settings } = useConfig();
  const { showError } = useToast();
  const [tab, setTab] = useState<Tab>('mini');

  const [mini, setMini] = useState<MiniData | null>(null);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState<RangeData | null>(null);
  const [detailLoaded, setDetailLoaded] = useState(false);

  const [year, setYear] = useState(currentYear);
  const [annual, setAnnual] = useState<RangeData | null>(null);

  useEffect(() => {
    setTab('mini');
    setMini(null);
    setDetail(null);
    setDetailLoaded(false);
    setAnnual(null);
  }, [memberId]);

  useEffect(() => {
    if (tab !== 'mini') return;
    api.get<MiniData>(`/members/${memberId}/statements/mini`).then((res) => setMini(res.data));
  }, [memberId, tab]);

  useEffect(() => {
    if (tab !== 'annual') return;
    api.get<RangeData>(`/members/${memberId}/statements/annual`, { params: { year } }).then((res) => setAnnual(res.data));
  }, [memberId, tab, year]);

  const loadDetail = async () => {
    if (!dateFrom || !dateTo) {
      showError('Choose a from and to date.');
      return;
    }
    try {
      const res = await api.get<RangeData>(`/members/${memberId}/statements/detail`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setDetail(res.data);
      setDetailLoaded(true);
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Could not load the statement.');
    }
  };

  const downloadMini = () =>
    downloadFile(api, `/members/${memberId}/statements/mini/pdf`, `Mini-Statement-${memberName}.pdf`);
  const downloadDetail = () =>
    downloadFile(api, `/members/${memberId}/statements/detail/pdf`, `Statement-${dateFrom}-to-${dateTo}-${memberName}.pdf`, {
      date_from: dateFrom,
      date_to: dateTo,
    });
  const downloadAnnual = () =>
    downloadFile(api, `/members/${memberId}/statements/annual/pdf`, `Annual-Statement-${year}-${memberName}.pdf`, { year });

  return (
    <>
      <div className="toolbar">
        <div className="tab-group">
          <button type="button" className={tab === 'mini' ? 'tab active' : 'tab'} onClick={() => setTab('mini')}>
            Mini
          </button>
          <button type="button" className={tab === 'detail' ? 'tab active' : 'tab'} onClick={() => setTab('detail')}>
            Detail
          </button>
          <button type="button" className={tab === 'annual' ? 'tab active' : 'tab'} onClick={() => setTab('annual')}>
            Annual
          </button>
        </div>
      </div>

      {tab === 'mini' && (
        <div className="card">
          {mini ? (
            <>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-item-label">Current balance</span>
                  <span className="detail-item-value" style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                    {formatMoney(mini.current_balance, settings.currency)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-item-label">As at</span>
                  <span className="detail-item-value">{mini.as_of}</span>
                </div>
              </div>
              <AccountSplit
                retirement={mini.retirement_balance}
                investment={mini.investment_balance}
                medisave={mini.medisave_balance}
                currency={settings.currency}
              />
              <TransactionTable transactions={mini.transactions} currency={settings.currency} />
              <div className="form-actions">
                <IconButton icon={IconDownload} label="Download PDF" onClick={downloadMini} />
              </div>
            </>
          ) : (
            <p className="muted">Loading…</p>
          )}
        </div>
      )}

      {tab === 'detail' && (
        <div className="card">
          <div className="toolbar">
            <label>
              From
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
            <button type="button" className="btn btn-primary" onClick={loadDetail}>
              View
            </button>
          </div>

          {detailLoaded && detail && (
            <>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-item-label">Opening balance</span>
                  <span className="detail-item-value">{formatMoney(detail.opening_balance, settings.currency)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-item-label">Closing balance</span>
                  <span className="detail-item-value">{formatMoney(detail.closing_balance, settings.currency)}</span>
                </div>
              </div>
              <AccountSplit
                retirement={detail.retirement_balance}
                investment={detail.investment_balance}
                medisave={detail.medisave_balance}
                currency={settings.currency}
              />
              <TransactionTable transactions={detail.transactions} currency={settings.currency} />
              <div className="form-actions">
                <IconButton icon={IconDownload} label="Download PDF" onClick={downloadDetail} />
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'annual' && (
        <div className="card">
          <div className="toolbar">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {annual ? (
            <>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-item-label">Opening balance (1 Jan {year})</span>
                  <span className="detail-item-value">{formatMoney(annual.opening_balance, settings.currency)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-item-label">Closing balance (31 Dec {year})</span>
                  <span className="detail-item-value">{formatMoney(annual.closing_balance, settings.currency)}</span>
                </div>
              </div>
              <AccountSplit
                retirement={annual.retirement_balance}
                investment={annual.investment_balance}
                medisave={annual.medisave_balance}
                currency={settings.currency}
              />
              <TransactionTable transactions={annual.transactions} currency={settings.currency} />
              <div className="form-actions">
                <IconButton icon={IconDownload} label="Download PDF" onClick={downloadAnnual} />
              </div>
            </>
          ) : (
            <p className="muted">Loading…</p>
          )}
        </div>
      )}
    </>
  );
}
